/**
 * Per-rep Zoom Scheduler custom field parameter names, task: extend Zoom
 * prefill to title/phone/topic, 2026-09-16.
 *
 * Zoom's own documented prefill mechanism (see buildZoomScheduleUrl in
 * EditContact.jsx) matches a query parameter to a booking page's field by
 * that field's own visible label, lowercased with everything but letters
 * stripped. firstname/lastname/email/companyname happen to be the same
 * across every rep's page, since those are Zoom's own standard fields (or,
 * for companyname, a custom field every rep happened to label the same
 * way). Title, phone and the meeting-topic field are not standard, and
 * each rep's own page has them under a different label, sometimes not at
 * all, so there is no single parameter name that works for all four.
 *
 * Every value here was found empirically, not guessed: loaded each rep's
 * real scheduler.zoom.us page (the raw URL now stored as their default
 * calendar_links entry, task: switch to raw Zoom URLs, 2026-09-15), picked
 * a date and time to reach the guest info form, and tried candidate
 * parameter names until one actually populated the field, confirmed by
 * reading the field's real DOM value back, not by inspecting the page's
 * visible text alone.
 *
 * A rep missing an entry here, or missing one of the three keys, is not a
 * bug: it means their page has no matching field, or (title/phone
 * specifically) the field exists but is a type Zoom does not appear to
 * support prefilling at all (see the phone note below). Zoom silently
 * ignores an unmatched parameter, so leaving a key out is always safe,
 * never sends a broken or blank value.
 *
 * If a rep's own Zoom Scheduler page changes (a custom question gets
 * renamed, added or removed), this needs a matching update, found the same
 * way: load their real page, try candidate names, confirm the one that
 * actually lands.
 */
export const ZOOM_SCHEDULER_FIELDS = {
  'adam@realtimefeedback.com': {
    title: 'positiontitle',
    // No working phone parameter found for Adam's page. His "Best Contact
    // Phone #" field is Zoom's own structured phone field type (a number
    // input paired with a separate country-code dropdown), not a plain
    // text custom field. Every plausible parameter name was tried
    // (bestcontactphone, bestcontactphonenumber, phone, phonenumber,
    // cellphone, cell) and none populated it; Mike's page has the exact
    // same field type under a different label and is equally unreachable,
    // while Marklen's differently-implemented plain-text phone field does
    // work. This looks like a real platform limitation on this field
    // type, not a naming problem.
    topic: 'meetingtopics',
  },
  'mike@realtimefeedback.com': {
    title: 'title',
    // Same structured phone field type as Adam's page, same result: no
    // parameter name populates it.
    topic: 'pleaseshareanythingthatwillhelpprepareforourmeeting',
  },
  'aaron@realtimefeedback.com': {
    // Aaron's page has no title field and no phone field at all, only the
    // name/email fields every page has plus this one open-ended question.
    topic: 'pleaseshareanythingthatwillhelpprepareforourmeeting',
  },
  'marklenk@realtimefeedback.com': {
    title: 'title',
    // Unlike Adam's and Mike's, Marklen's phone field is a plain text
    // custom field, not Zoom's structured phone field type, which is
    // exactly why this one is reachable when theirs are not.
    phone: 'bestcontactphone',
    topic: 'pleaseshareanythingthatwillhelpprepareforourmeeting',
  },
}

/**
 * Sent as every rep's meeting-topic prefill value, when their page has a
 * field mapped for it above. Fixed and identical for every booking, not
 * drawn from the contact: the ask here was a single standing line, not a
 * per-contact topic.
 */
export const ZOOM_MEETING_TOPIC = 'Continue discussing Real Time Feedback'
